import { Effect, EffectProperties, EffectFields } from '../../core/Effect';
import * as PostProcessing from '@react-three/postprocessing';
import { forwardRef } from 'react';
import * as THREE from 'three';
import { Field } from '@/types/core/Field';
import { BlendFunction } from 'postprocessing';

export interface SSAOEffectProperties extends EffectProperties {
  intensity: number;
  samples: number;
  rings: number;
  radius: number;
  distanceThreshold: number;
  distanceFalloff: number;
  rangeThreshold: number;
  rangeFalloff: number;
  luminanceInfluence: number;
  color: string;
}

// Create a reusable SSAO component that can be used in both preview and world
const SSAOComponent = forwardRef<any, { properties: SSAOEffectProperties; isPreview?: boolean }>((props, ref) => {
  const { properties, isPreview } = props;  

  if (isPreview) {
    return null;
  }
  
  const { 
    blendFunction,
    intensity, 
    samples, 
    rings,
    radius, 
    distanceThreshold,
    distanceFalloff,
    rangeThreshold,
    rangeFalloff,
    luminanceInfluence,
    color
  } = properties;

  // Convert blendFunction string to number if it exists
  const blendFunctionValue = blendFunction ? 
    BlendFunction[blendFunction as keyof typeof BlendFunction] : 
    BlendFunction.MULTIPLY;

  return (
    <PostProcessing.SSAO
      ref={ref}
      blendFunction={blendFunctionValue}
      intensity={intensity}
      samples={samples}
      rings={rings}
      radius={radius}
      worldDistanceThreshold={distanceThreshold}
      worldDistanceFalloff={distanceFalloff}
      worldProximityThreshold={rangeThreshold}
      worldProximityFalloff={rangeFalloff}
      luminanceInfluence={luminanceInfluence}
      color={color ? new THREE.Color(color) : undefined}
    />
  );
});

export const SSAOEffectItem: Effect<SSAOEffectProperties> = {
  id: '',
  name: 'SSAO',
  type: 'effect',
  subtype: 'SSAO',
  icon: 'Effect',
  properties: {
    blendFunction: 'MULTIPLY',
    intensity: 1.5,
    samples: 21,
    rings: 4,
    radius: 7,
    distanceThreshold: 0.4,
    distanceFalloff: 0.1,
    rangeThreshold: 0.5,
    rangeFalloff: 0.1,
    luminanceInfluence: 0.7,
    color: '#000000',
  },
  fields: [
    ...EffectFields,
    {
      key: 'properties.blendFunction',
      type: 'select',
      label: 'Blend Function',
      default: 'MULTIPLY',
      options: [
        { value: 'NORMAL', label: 'Normal' },
        { value: 'ADD', label: 'Add' },
        { value: 'SUBTRACT', label: 'Subtract' },
        { value: 'MULTIPLY', label: 'Multiply' },
        { value: 'SCREEN', label: 'Screen' },
        { value: 'OVERLAY', label: 'Overlay' },
      ],
      panel: 'effect',
    },
    {
      key: 'properties.intensity',
      type: 'number',
      label: 'Intensity',
      default: 1.5,
      panel: 'effect',
    },
    {
      key: 'properties.samples',
      type: 'number',
      label: 'Samples',
      default: 21,
      panel: 'effect',
    },
    {
      key: 'properties.rings',
      type: 'number',
      label: 'Rings',
      default: 4,
      panel: 'effect',
    },
    {
      key: 'properties.radius',
      type: 'number',
      label: 'Radius',
      default: 7,
      panel: 'effect',
    },
    {
      key: 'properties.distanceThreshold',
      type: 'number',
      label: 'Distance Threshold',
      default: 0.4,
      panel: 'effect',
    },
    {
      key: 'properties.distanceFalloff',
      type: 'number',
      label: 'Distance Falloff',
      default: 0.1,
      panel: 'effect',
    },
    {
      key: 'properties.rangeThreshold',
      type: 'number',
      label: 'Range Threshold',
      default: 0.5,
      panel: 'effect',
    },
    {
      key: 'properties.rangeFalloff',
      type: 'number',
      label: 'Range Falloff',
      default: 0.1,
      panel: 'effect',
    },
    {
      key: 'properties.luminanceInfluence',
      type: 'number',
      label: 'Luminance Influence',
      default: 0.7,
      panel: 'effect',
    },
    {
      key: 'properties.color',
      type: 'color',
      label: 'Color',
      default: '#000000',
      panel: 'effect',
    }
  ],
  render: (properties: SSAOEffectProperties, isPreview: boolean, ref: React.RefObject<THREE.Object3D | null>) => {
    return <SSAOComponent properties={properties} isPreview={isPreview} ref={ref as any} />;
  }
}; 